// src/components/assistant/attachments.tsx
//
// 📎 Adjuntos del chat (portado de WebChat, misma API y mismos límites).
//
// 🔒 SINGLE-SOURCE · el upload de archivos del chat tiene DOS destinos y ninguno
// se inventa storage paralelo:
//   · hoja de cálculo (.xlsx/.csv…) → apps/ingest  (staging + perfil + tools)
//   · cualquier otro archivo        → apps/documents (Drive multi-tenant)
// Un Excel metido al Drive hace que el agente lo lea completo e invente valores a
// la mitad; en ingesta el agente recibe solo el PERFIL (~200 tokens) y tools para
// consultarlo con filtros. La org sale del JWT (mismos clientes de api que WebChat).
//
// 🔒 SINGLE-SOURCE · el TAG es para el agente, la MINIATURA para el humano. El
// document_id / dataset_id viaja dentro del texto del mensaje a propósito: es lo
// que el agente usa para leer el archivo con sus tools y deja el historial
// auto-descriptivo al recargar. Al PINTAR se extrae del texto y se sustituye por
// la tarjeta/imagen. Cambiar el formato del tag exige actualizar los RE_* de abajo,
// o los mensajes viejos vuelven a mostrar el tag crudo.

import { useEffect, useState } from 'react';
import { FileText, Table2 } from 'lucide-react';
import { getDocument, uploadDocument } from '@/modules/documents/api';
import type { DocumentOut } from '@/modules/documents/types';
import { isTabularFile, uploadDataset } from '@/modules/ingest/api';

/** Tipos aceptados por el clip (los mismos que tenía WebChat). */
export const ATTACH_ACCEPT = '.pdf,.doc,.docx,.pptx,.txt,.md,.csv,.tsv,.xlsx,.xls,.xlsm,.png,.jpg,.jpeg,.webp';

const tagDoc = (titulo: string, id: string) => `📎 Adjunté «${titulo}» a Mis Documentos (document_id: ${id}). `;
const RE_DOC = /📎 Adjunté «(.*?)» a Mis Documentos \(document_id: ([^)]+)\)\.?[ \t]*/gu;
// Formato que usó ChatPane entre #238 y la unificación (mensajes ya guardados).
const RE_DOC_V0 = /📎 Adjunté «(.*?)» \(document_id: ([^)]+)\)\. Léelo con las tools de documentos\.[ \t]*/gu;
const RE_DATASET = /📊 Cargué «(.*?)» \(dataset_id: ([^)]+)\)[^]*?\(no supongas valores\)\.[ \t]*/gu;

/** Adjunto en el compositor, antes de enviar. */
export interface PendingAttachment {
  id: string;
  titulo: string;
  tag: string;
  clase: 'doc' | 'dataset';
  /** objectURL local (solo imágenes); hay que revocarlo. */
  preview?: string;
}

/** Bloque solo-cliente: se genera al pintar, NUNCA se envía al API. */
export interface AttachmentView {
  tipo: 'adjunto';
  clase: 'doc' | 'dataset';
  refId: string;
  titulo: string;
  detalle?: string;
}

/** Sube el archivo al destino correcto y devuelve el adjunto pendiente. */
export async function uploadChatAttachment(file: File): Promise<PendingAttachment> {
  if (isTabularFile(file)) {
    const { dataset, profile } = await uploadDataset(file);
    // Las columnas van en el tag: sin ellas el agente pregunta «¿qué columnas
    // tiene?» o, peor, adivina nombres que no existen.
    const cols = (profile?.columns ?? []).map((c) => c.key).join(', ');
    const tag =
      `📊 Cargué «${dataset.filename}» (dataset_id: ${dataset.id}) — ` +
      `${dataset.row_count} filas. Columnas: ${cols}. ` +
      `Consúltalo con las tools de ingest (no supongas valores). `;
    return { id: String(dataset.id), titulo: dataset.filename, tag, clase: 'dataset' };
  }
  // visibility 'org' (no 'personal'): con JWT de Orión no hay user Django →
  // owner_user queda NULL y un doc personal sin owner es invisible en el Drive.
  const doc = await uploadDocument({
    title: file.name,
    description: 'Subido desde el chat del agente',
    tags: ['chat'],
    visibility: 'org',
    file,
  });
  return {
    id: String(doc.id),
    titulo: doc.title,
    tag: tagDoc(doc.title, doc.id),
    clase: 'doc',
    // La miniatura aparece al instante y no depende de que el CDN ya lo sirva.
    preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
  };
}

/** Saca los tags de un texto: devuelve los adjuntos y el texto limpio. */
export function splitAttachments(texto: string): { adjuntos: AttachmentView[]; resto: string } {
  const adjuntos: AttachmentView[] = [];
  const doc = (_m: string, titulo: string, id: string) => {
    adjuntos.push({ tipo: 'adjunto', clase: 'doc', refId: id.trim(), titulo });
    return '';
  };
  let resto = texto.replace(RE_DOC, doc).replace(RE_DOC_V0, doc);
  resto = resto.replace(RE_DATASET, (m: string, titulo: string, id: string) => {
    const filas = /— ([\d.,]+) filas/.exec(m)?.[1];
    adjuntos.push({ tipo: 'adjunto', clase: 'dataset', refId: id.trim(), titulo, detalle: filas ? `${filas} filas` : undefined });
    return '';
  });
  return { adjuntos, resto: resto.trim() };
}

// El tag solo trae el id; el file_url hay que resolverlo contra el Drive.
// Sin caché, cada re-render del hilo dispara un GET por cada adjunto.
const docCache = new Map<string, Promise<DocumentOut | null>>();
function docMeta(id: string): Promise<DocumentOut | null> {
  if (!docCache.has(id)) docCache.set(id, getDocument(id).catch(() => null));
  return docCache.get(id)!;
}

/** Adjunto ya enviado: imagen si es imagen; si no, tarjeta con el nombre (nunca el id). */
export function AttachmentCard({ bloque }: { bloque: AttachmentView }) {
  const [doc, setDoc] = useState<DocumentOut | null>(null);
  const [resuelto, setResuelto] = useState(bloque.clase !== 'doc');

  useEffect(() => {
    if (bloque.clase !== 'doc') return;
    let vivo = true;
    void docMeta(bloque.refId).then((d) => {
      if (!vivo) return;
      setDoc(d);
      setResuelto(true);
    });
    return () => {
      vivo = false;
    };
  }, [bloque.clase, bloque.refId]);

  if (doc?.file_type === 'image' && doc.file_url) {
    return (
      <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={doc.file_url}
          alt={bloque.titulo}
          title={bloque.titulo}
          loading="lazy"
          className="max-h-60 max-w-full rounded-lg border border-border sm:max-w-xs"
        />
      </a>
    );
  }
  const Icono = bloque.clase === 'dataset' ? Table2 : FileText;
  const detalle = bloque.detalle ?? (resuelto ? doc?.file_type?.toUpperCase() : undefined);
  const cuerpo = (
    <>
      <Icono size={16} className="shrink-0 text-primary" aria-hidden />
      <span className="min-w-0 truncate text-xs font-medium">{bloque.titulo}</span>
      {detalle && <span className="shrink-0 text-[11px] text-muted-foreground">{detalle}</span>}
    </>
  );
  const cls = 'flex max-w-full items-center gap-2 rounded-lg border border-border bg-background/60 px-2.5 py-2 text-foreground';
  return doc?.file_url ? (
    <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className={`${cls} hover:bg-muted`}>
      {cuerpo}
    </a>
  ) : (
    <div className={cls}>{cuerpo}</div>
  );
}
